import type { GlobalStylesProps } from "@mui/material/GlobalStyles";
import theme, { makeStyles, ThemeColors } from "core/utilities/theme";
import { sequencePixelHeight } from "sequenceAnalyser/utilities/screen/screen";

// z-index order so elements overlap properly (from foreground to background):
export const HOVER_TOOLTIP_Z_INDEX = 9999;
export const ZOOM_COMMANDS_Z_INDEX = 12;
export const VISUALISATIONS_Z_INDEX = 11;
const TITLE_Z_INDEX = 10;
export const SELECTION_OVERLAY_Z_INDEX = 9; // must be lower than TITLE_Z_INDEX
const SEARCH_MATCH_INDEX = 4;
const RESIDUE_Z_INDEX = 3;

interface MakeStylesProps {
  sidebarPixelWidth: number;
  visualisationsHeight: number;
}

export const selectionStyles: GlobalStylesProps["styles"] = {
  "@keyframes selectionOverlayAnimation": {
    "0%": {
      outline: `1px dashed ${ThemeColors.sequenceAnalyserSelectionDashedBorder[0]}`,
      boxShadow: `0 0 0 1px ${ThemeColors.sequenceAnalyserSelectionDashedBorder[1]}`,
    },
    "50%": {
      outline: `1px dashed ${ThemeColors.sequenceAnalyserSelectionDashedBorder[0]}`,
      boxShadow: `0 0 0 1px ${ThemeColors.sequenceAnalyserSelectionDashedBorder[1]}`,
    },
    "51%": {
      outline: `1px dashed ${ThemeColors.sequenceAnalyserSelectionDashedBorder[1]}`,
      boxShadow: `0 0 0 1px ${ThemeColors.sequenceAnalyserSelectionDashedBorder[0]}`,
    },
    "100%": {
      outline: `1px dashed ${ThemeColors.sequenceAnalyserSelectionDashedBorder[1]}`,
      boxShadow: `0 0 0 1px ${ThemeColors.sequenceAnalyserSelectionDashedBorder[0]}`,
    },
  },
  // element created on the fly in selection.ts
  "#selectionOverlay": {
    position: "absolute",
    zIndex: SELECTION_OVERLAY_Z_INDEX,
    background: `${ThemeColors.sequenceAnalyserSelection}`,
    animationDuration: "0.4s",
    animationName: "selectionOverlayAnimation",
    animationIterationCount: "infinite",
    animationDirection: "alternate",
    pointerEvents: "none",
  },
};

export default makeStyles((props: MakeStylesProps) => ({
  sequences: {
    display: "inline-block",
    // Disable browser's native selection as we have developed built-in tools
    // to select sequences, copy sequences etc.
    userSelect: "none",
    "& .sequence, & .sequenceFeature": {
      whiteSpace: "nowrap",
      "& .residuesWrapper, & .featureWrapper": {
        transformOrigin: `-${props.sidebarPixelWidth}px 0`,
      },
      "& .title": {
        position: "sticky",
        transformOrigin: "0 0",
        left: 0,
        display: "inline-block",
        width: `${props.sidebarPixelWidth}px`,
        padding: "0 8px",
        zIndex: TITLE_Z_INDEX,
        backgroundColor: theme.palette.background.default,
        borderRight: `1px solid ${ThemeColors.sequenceAnalyserBorder}`,
        "& .name": {
          display: "flex",
          verticalAlign: "bottom",
          width: "100%",
          marginRight: "4px",
          overflow: "hidden",
          "& span:first-of-type": {
            flexGrow: 1,
            overflow: "hidden",
          },
        },
      },
      "& button": {
        padding: "0 !important",
        height: "20px",
        marginTop: "-2px",
      },
      "& .feature": {
        position: "absolute",
        height: `${sequencePixelHeight(false, true)}px`,
      },
      "& .searchMatch": {
        position: "absolute",
        zIndex: SEARCH_MATCH_INDEX,
        boxShadow: "0 0 0 3px rgba(0,0,0,0.6)",
        height: `${sequencePixelHeight(false, true) - 1}px`,
        // Allow hover to happens through the element
        pointerEvents: "none",
        "&.active": {
          boxShadow: "0 0 0 5px rgba(0,0,0,1)",
        },
      },
      "& .selectable": {
        position: "relative",
        zIndex: RESIDUE_Z_INDEX,
        display: "inline-block",
        width: "18px",
        textAlign: "center",
        margin: "0",
        padding: "0 1px",
        backgroundColor: "rgba(255, 255, 255, 0.5)",
        borderRight: "1px solid #e7ecef",
        borderBottom: "1px solid #e7ecef",
        "&.darkBg": {
          color: "white",
          "&:hover": {
            boxShadow: "inset 0 0 0 2px white",
          },
        },
        "&.lightBg": {
          color: theme.palette.text.primary,
          "&:hover": {
            boxShadow: `inset 0 0 0 2px ${theme.palette.text.primary}`,
          },
        },
        // Overlap of features when colors are set together with features
        "& > .thinFeatureStrippes": {
          position: "absolute",
          width: "100%",
          height: "5px",
          bottom: "-5px",
          "&.lightBg": {
            background:
              "repeating-linear-gradient(-45deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.5) 1px, rgba(255, 255, 255, 0) 2px, rgba(255, 255, 255, 0) 4px)",
          },
          "&.darkBg": {
            background:
              "repeating-linear-gradient(-45deg, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0) 1px, rgba(0, 0, 0, 0.2) 2px, rgba(0, 0, 0, 0.2) 3px)",
          },
        },
        "& > .thinFeatureOverlay": {
          position: "absolute",
          width: "calc(100% + 2px)",
          height: "5px",
          bottom: "-5px",
          left: "-1px",
          background: `rgb(${ThemeColors.sequenceAnalyserAbbreviatedResidue.join(", ")})`,
        },
        "&.searchMatch": {
          boxShadow: "inset 0 0 0 2px rgba(0,0,0,0.7)",
        },
        "&.searchActiveMatch": {
          boxShadow: "inset 0 0 0 3px rgba(0,0,0,1)",
        },
      },
      "&:last-child": {
        "& .selectable": {
          borderBottom: 0,
        },
      },
      // Full overlap, when colors and features are not set together
      "& .lightFeatureStrippes": {
        background:
          "repeating-linear-gradient(-45deg, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0) 4px, rgba(255, 255, 255, 0.3) 3px, rgba(255, 255, 255, 0.3) 5px)",
      },
      "& .darkFeatureStrippes": {
        background:
          "repeating-linear-gradient(-45deg, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0) 4px, rgba(0, 0, 0, 0.2) 5px, rgba(0, 0, 0, 0.2) 5px)",
      },
    },
    "& .sequence": {
      "& .title": {
        cursor: "pointer",
        "&:hover": {
          backgroundColor: ThemeColors.sequenceAnalyserSelection,
          "& .name": {
            backgroundColor: ThemeColors.sequenceAnalyserSelection,
          },
        },
        "&.selectedRow": {
          background: `${ThemeColors.sequenceAnalyserSelection} !important`,
          "&:hover": {
            "& .name": {
              background: `${ThemeColors.sequenceAnalyserSelection} !important`,
            },
          },
        },
        "&.draggingOver": {
          boxShadow: `0 -1px 0 ${ThemeColors.dropPlaceholder}`,
        },
      },
    },
    "&.withVisualisations": {
      paddingBottom: `${props.visualisationsHeight}px`,
    },
    "&.withColors": {
      "& .sequence": {
        "& .title": {
          height: `${sequencePixelHeight(true, false)}px`,
        },
      },
    },
    "&.withFeatures": {
      "& .sequence": {
        "& .title": {
          height: `${sequencePixelHeight(false, true)}px`,
        },
      },
    },
    // When both colors and features are displayed, the height of the sequence is slightly bigger
    "&.withColors.withFeatures": {
      "& .sequence": {
        "& .feature": {
          height: "5px",
          bottom: 0,
        },
        "& .searchMatch": {
          marginTop: "2px",
          height: `${sequencePixelHeight(true, true) - 2}px`,
        },
        "& .selectable": {
          fontSize: "13px",
          fontWeight: 500,
          borderBottom: 0,
        },
        "& .title": {
          height: `${sequencePixelHeight(true, true)}px`,
        },
      },
    },
    "& .sequenceFeature": {
      "& .title": {
        "&.darkBg": {
          color: "white",
        },
        "&.lightBg": {
          color: theme.palette.text.primary,
        },
      },
    },
  },
}));
